import React, { useState, useEffect } from "react";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { fetchGalleries } from "../../redux/features/media/galleriesSlice";

// Components
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ListSubheader from "@material-ui/core/ListSubheader";
import { VariableSizeList } from "react-window";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import Skeleton from "@material-ui/lab/Skeleton";

const LISTBOX_PADDING = 10; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 60;

  const getChildSize = child => {
    if (React.isValidElement(child) && child.type === ListSubheader) {
      return 48;
    }
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 100 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          key={itemCount}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={index => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}>
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const GalleryComboBox = ({ onChange, defaultValue, readOnly }) => {
  // Local state
  const [value, setValue] = useState(defaultValue || null);

  // Redux
  const dispatch = useDispatch();
  const { galleriesList, isLoadingGallery, errorGallery } = useSelector(
    state => state.galleries
  );

  useEffect(() => {
    // Load Hotels
    dispatch(fetchGalleries());
  }, [dispatch]);

  useEffect(() => {
    if (value) {
      onChange(value);
    } else {
      onChange(null);
    }
  }, [onChange, value]);

  if (errorGallery) {
    return (
      <Typography
        variant="body1"
        color="error"
        style={{ display: "flex", alignItems: "flex-end", height: "100%" }}
        component="div">
        There was an error loading the Gallery Module.
      </Typography>
    );
  }

  return (
    <>
      {!isLoadingGallery && galleriesList.length > 0 ? (
        <Autocomplete
          id="gallery-select"
          options={galleriesList}
          autoHighlight
          clearOnEscape
          loading={isLoadingGallery}
          getOptionLabel={gallery => gallery.name}
          value={
            value && galleriesList.find(gallery => gallery.id === value.id)
          }
          onChange={(event, newValue) => {
            setValue(newValue);
          }}
          onInputChange={(e, value, reason) => {
            if (reason === "clear") {
              setValue(null);
            }
          }}
          disabled={readOnly}
          disableListWrap
          autoComplete
          ListboxComponent={ListboxComponent}
          renderOption={gallery => (
            <Typography component="div">
              <Box>{gallery.name}</Box>
              <Box fontWeight="fontWeightLight" fontSize="body2.fontSize">
                {gallery.photoQty} photos.
              </Box>
            </Typography>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Choose a Gallery"
              fullWidth
              required
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
        />
      ) : (
        <Skeleton variant="rect" width={"100%"} height={48} animation="wave" />
      )}
    </>
  );
};

export default GalleryComboBox;
